import { graphql, PageProps } from 'gatsby';
import React from 'react';

import Seo from '../components/utils/seo';
import ServiceLdJson from '../components/utils/service-ld-json';
import { extractFaqData } from '../utils/extract-faq-data';
import BlockRenderer, { BlockRendererProps } from '../components/block-renderer';
import { FranchisePagePageContext, FranchisePagePageData } from './franchise/franchise-page';
import { FranchiseServicePageContext, FranchiseServicePageData } from './franchise/franchise-service';
import { useLiveUpdateProvider } from '../components/live-update/provider';
import { useMicrosite } from '../contexts/franchise/microsite-context';
import { PageTemplate } from '../utils/match-page-template';

/* Each block should output a <section> into the <main> from <Layout> */

export type DefaultTemplatePageData = Queries.DefaultTemplateQuery;

export interface DefaultTemplatePageContext {
    id: string;
    slug: string
    templateName: PageTemplate.DefaultPageBuilder
}

type DefaultTemplatePageDataUnion =
    | DefaultTemplatePageData['csPageBuilder']
    | DefaultTemplatePageData['csAbout']
    | DefaultTemplatePageData['csCommunityPage']
    | DefaultTemplatePageData['csCorporateCareers']
    | DefaultTemplatePageData['csCustomerReviewsPage']
    | DefaultTemplatePageData['csFranchiseDisclosureDocument']
    | DefaultTemplatePageData['csOwnAFranchise']
    | DefaultTemplatePageData['csIngredientsDisclosurePage']
    | DefaultTemplatePageData['csInsurerPartners']
    | DefaultTemplatePageData['csInsurerProfessionals']
    | DefaultTemplatePageData['csPressPhotos']
    | DefaultTemplatePageData['csLeadershipIndex']
    | DefaultTemplatePageData['csLocation']
    | DefaultTemplatePageData['csServiceDetail']
    | DefaultTemplatePageData['csFaqsPage']
    | FranchiseServicePageData['csFranchiseServicePages']
    | FranchisePagePageData['csFranchisePageBuilder'];

const DefaultTemplate: React.FC<Omit<PageProps<
DefaultTemplatePageData | Pick<FranchisePagePageData, 'csFranchisePageBuilder'> | Pick<FranchiseServicePageData, 'csFranchiseServicePages'>,
DefaultTemplatePageContext | FranchisePagePageContext | FranchiseServicePageContext
>, 'children'> & {
    children?: React.ReactNode | React.ReactNode[]
}> = ({
    children,
    data: originalData,
}) => {
    const { isMicrosite } = useMicrosite();
    const { data } = useLiveUpdateProvider(originalData, isMicrosite);
    const pageData = Object.values(data).find(x => !!x) as DefaultTemplatePageDataUnion;

    if (!pageData) {
        return null;
    }

    const {
        sections, title, seo,
    } = pageData;

    // Check if this is a ServiceDetail page
    const isServiceDetail = 'linkedServiceTypes' in pageData;
    const serviceDetailData = isServiceDetail ? (pageData as DefaultTemplatePageData['csServiceDetail'] & {
        linkedServiceTypes?: ReadonlyArray<{ title?: string | null } | null> | null;
    }) : null;

    // Extract FAQ data and generate JSON-LD
    const { faqJsonLd } = extractFaqData(pageData);
    return (
        <>
            {faqJsonLd && (
                <script
                    type="application/ld+json"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(faqJsonLd) || '',
                    }}
                />
            )}
            {serviceDetailData && (
                <ServiceLdJson
                    title={serviceDetailData.title}
                    description={serviceDetailData.seo?.metaDescription}
                    url={serviceDetailData.url}
                    linkedServiceTypes={serviceDetailData.linkedServiceTypes}
                />
            )}
            <Seo
                title={seo?.metaTitle ?? title ?? undefined}
                description={seo?.metaDescription ?? undefined}
                imageUrl={seo?.metaImage?.url ?? undefined}
            />
            {sections && (<BlockRenderer data={sections as BlockRendererProps['data']} />)}
            {children || null}
        </>
    );
};

export const query = graphql`
    query DefaultTemplate ($slug: String!) {
        csPageBuilder(url: { eq: $slug }) {
            title
            url
            seo {
                metaTitle
                metaDescription
                metaImage {
                    url
                }
            }
            pageOptions {
                hideNationalCallCenter
                hideLocalCta
                overridePhoneNumber
                overridePhoneNumberDescriptor
            }
            publishDetails {
                publishedAt
            }
            sections {
                __typename
                ...BlogrollData
                ...CommunityBannerData
                ...FaqsData
                ...GalleryData
                ...HeroData
                ...IconBulletsData
                ...MediaContentData
                ...MediacontentFaqsData
                ...MultiBlogrollData
                ...RichtextBlockData
                ...SliderData
                ...ServicesSliderData
                ...SplitTextBannerData
                ...SubnavStripeData
                ...TeamMemberGridData
                ...TestimonialData
                ...TextBannerData
                ...FranchiseDisclosureData
                ...ServicesDetailData
                ...FormData
                ...AnchorData
                ...IngredientsDisclosureData
            }
        }
        csAbout(url: { eq: $slug }) {
            title
            url
            seo {
                metaTitle
                metaDescription
                metaImage {
                    url
                }
            }
            sections {
                __typename
                ...HeroData
                ...IconBulletsData
                ...MediaContentData
                ...SliderData
            }
            publishDetails {
                publishedAt
            }
        }
        csCommunityPage(url: { eq: $slug }) {
            title
            url
            seo {
                metaTitle
                metaDescription
                metaImage {
                    url
                }
            }
            sections {
                __typename
                ...BlogrollData
                ...CommunityBannerData
                ...HeroData
                ...MediaContentData
                ...TextBannerData
            }
            publishDetails {
                publishedAt
            }
        }
        csCorporateCareers(url: { eq: $slug }) {
            title
            url
            seo {
                metaTitle
                metaDescription
                metaImage {
                    url
                }
            }
            sections {
                __typename
                ...HeroData
                ...IconBulletsData
                ...MediaContentData
                ...SliderData
                ...FaqsData
            }
            publishDetails {
                publishedAt
            }
        }
        csCustomerReviewsPage(url: { eq: $slug }) {
            title
            url
            seo {
                metaTitle
                metaDescription
                metaImage {
                    url
                }
            }
            sections {
                __typename
                ...HeroData
                ...SliderData
                ...TextBannerData
            }
            publishDetails {
                publishedAt
            }
        }
        csFranchiseDisclosureDocument(url: { eq: $slug }) {
            title
            url
            seo {
                metaTitle
                metaDescription
                metaImage {
                    url
                }
            }
            sections {
                __typename
                ...FranchiseDisclosureData
            }
            publishDetails {
                publishedAt
            }
        }
        csOwnAFranchise(url: { eq: $slug }) {
            title
            url
            seo {
                metaTitle
                metaDescription
                metaImage {
                    url
                }
            }
            sections {
                __typename
                ...FaqsData
                ...HeroData
                ...IconBulletsData
                ...MediaContentData
                ...SliderData
                ...TextBannerData
            }
            publishDetails {
                publishedAt
            }
        }
        csIngredientsDisclosurePage(url: { eq: $slug }) {
            title
            url
            seo {
                metaTitle
                metaDescription
                metaImage {
                    url
                }
            }
            sections {
                __typename
                ...IngredientsDisclosureData
            }
            publishDetails {
                publishedAt
            }
        }
        csInsurerPartners(url: { eq: $slug }) {
            title
            url
            seo {
                metaTitle
                metaDescription
                metaImage {
                    url
                }
            }
            sections {
                __typename
                ...HeroData
                ...MediaContentData
                ...SliderData
            }
            publishDetails {
                publishedAt
            }
        }
        csInsurerProfessionals(url: { eq: $slug }) {
            title
            url
            seo {
                metaTitle
                metaDescription
                metaImage {
                    url
                }
            }
            sections {
                __typename
                ...HeroData
                ...IconBulletsData
                ...MediaContentData
                ...SliderData
            }
            publishDetails {
                publishedAt
            }
        }
        csPressPhotos(url: { eq: $slug }) {
            title
            url
            seo {
                metaTitle
                metaDescription
                metaImage {
                    url
                }
            }
            sections {
                __typename
                ...HeroData
                ...GalleryData
                ...SubnavStripeData
                ...TextBannerData
            }
            publishDetails {
                publishedAt
            }
        }
        csLeadershipIndex(url: { eq: $slug }) {
            title
            url
            seo {
                metaTitle
                metaDescription
                metaImage {
                    url
                }
            }
            sections {
                __typename
                ...HeroData
                ...TeamMemberGridData
            }
            publishDetails {
                publishedAt
            }
        }
        csLocation(url: { eq: $slug }) {
            title
            url
            seo {
                metaTitle
                metaDescription
                metaImage {
                    url
                }
            }
            sections {
                __typename
                ...HeroData
                ...IconBulletsData
                ...MediaContentData
                ...SliderData
            }
            publishDetails {
                publishedAt
            }
        }
        csServiceDetail(url: { eq: $slug }) {
            title
            url
            linkedServiceTypes {
                title
            }
            seo {
                metaTitle
                metaDescription
                metaImage {
                    url
                }
            }
            sections {
                __typename
                # ...BasicCtaData This doesn't exist as a block, have to get rid of it on contentstack
                ...BlogrollData
                ...HeroData
                ...IconBulletsData
                ...MediaContentData
                ...MediacontentFaqsData
                ...ServicesDetailData
                ...SliderData
                ...SplitTextBannerData
                ...TestimonialData
                ...TextBannerData
                ...FormData
                ...FaqsGridData
            }
            publishDetails {
                publishedAt
            }
        }
        csFaqsPage(url: { eq: $slug }) {
            title
            url
            seo {
                metaTitle
                metaDescription
                metaImage {
                    url
                }
            }
            sections {
                __typename
                ...FaqsData
                ...SubnavStripeData
            }
            publishDetails {
                publishedAt
            }
        }
    }
`;

export default DefaultTemplate;
